import axios from 'axios';

import {
  parseApproveDpiaForBE,
  parseDpiaDetailResponseForFE,
  parseMeasureForBE,
  parseMeasureForFE,
  parseRequestDpiaClarificationForBE,
  parseRiskForBE,
  parseRiskMutationResponseForFE,
  readDpiaRevisionFromResponse,
  parseSubmitDpiaForBE,
  parseUpdateDpiaForBE,
} from './dpias.parsers';
import type {
  ApproveDpiaInput,
  CreateDpiaMeasureInput,
  CreateDpiaRiskInput,
  CreateDpiaRiskOptions,
  DpiaDetailResponseDTO,
  DpiaMeasureDTO,
  DpiasApi,
  RequestDpiaClarificationInput,
  UpdateDpiaMeasureInput,
  UpdateDpiaRiskInput,
} from './dpias.types';

const buildDetailEndpoint = (organizationId: number, dpiaId: number) => `api/v1/organizations/${organizationId}/dpias/${dpiaId}/`;

const buildReviewEndpoint = (organizationId: number, dpiaId: number) => `${buildDetailEndpoint(organizationId, dpiaId)}review/`;

const buildSubmitEndpoint = (organizationId: number, dpiaId: number) => `${buildDetailEndpoint(organizationId, dpiaId)}submit/`;

const buildApproveEndpoint = (organizationId: number, dpiaId: number) => `${buildDetailEndpoint(organizationId, dpiaId)}approve/`;

const buildRequestClarificationEndpoint = (organizationId: number, dpiaId: number) =>
  `${buildDetailEndpoint(organizationId, dpiaId)}request-clarification/`;

const buildRisksEndpoint = (organizationId: number, dpiaId: number) => `${buildDetailEndpoint(organizationId, dpiaId)}risks/`;

const buildRiskEndpoint = (organizationId: number, dpiaId: number, riskId: number) =>
  `${buildRisksEndpoint(organizationId, dpiaId)}${riskId}/`;

const buildMeasuresEndpoint = (organizationId: number, dpiaId: number) => `${buildDetailEndpoint(organizationId, dpiaId)}measures/`;

const buildMeasureEndpoint = (organizationId: number, dpiaId: number, measureId: number) =>
  `${buildMeasuresEndpoint(organizationId, dpiaId)}${measureId}/`;

export const dpiasApi: DpiasApi = {
  getDetail: async (organizationId, dpiaId) => {
    const { data } = await axios.get(buildDetailEndpoint(organizationId, dpiaId));
    return { data: parseDpiaDetailResponseForFE(data) };
  },
  getReviewDetail: async (organizationId, dpiaId) => {
    const { data } = await axios.get(buildReviewEndpoint(organizationId, dpiaId));
    return { data: parseDpiaDetailResponseForFE(data) };
  },
  update: async (organizationId, dpiaId, payload) => {
    const { data } = await axios.patch<DpiaDetailResponseDTO>(buildDetailEndpoint(organizationId, dpiaId), parseUpdateDpiaForBE(payload));
    return { data: parseDpiaDetailResponseForFE(data) };
  },
  createRisk: async (organizationId, dpiaId, payload: CreateDpiaRiskInput, options?: CreateDpiaRiskOptions) => {
    const { data } = await axios.post(buildRisksEndpoint(organizationId, dpiaId), parseRiskForBE(payload));
    return {
      data: parseRiskMutationResponseForFE(data, {
        payload,
        knownRiskIds: options?.knownRiskIds,
      }),
      revision: readDpiaRevisionFromResponse(data),
    };
  },
  updateRisk: async (organizationId, dpiaId, riskId, payload: UpdateDpiaRiskInput) => {
    const { data } = await axios.patch(buildRiskEndpoint(organizationId, dpiaId, riskId), parseRiskForBE(payload));
    return { data: parseRiskMutationResponseForFE(data, { riskId }), revision: readDpiaRevisionFromResponse(data) };
  },
  deleteRisk: async (organizationId, dpiaId, riskId) => {
    await axios.delete(buildRiskEndpoint(organizationId, dpiaId, riskId));
  },
  createMeasure: async (organizationId, dpiaId, payload: CreateDpiaMeasureInput) => {
    const { data } = await axios.post(buildMeasuresEndpoint(organizationId, dpiaId), parseMeasureForBE(payload));
    return { data: parseMeasureForFE(data as DpiaMeasureDTO), revision: readDpiaRevisionFromResponse(data) };
  },
  updateMeasure: async (organizationId, dpiaId, measureId, payload: UpdateDpiaMeasureInput) => {
    const { data } = await axios.patch(buildMeasureEndpoint(organizationId, dpiaId, measureId), parseMeasureForBE(payload));
    return { data: parseMeasureForFE(data as DpiaMeasureDTO), revision: readDpiaRevisionFromResponse(data) };
  },
  deleteMeasure: async (organizationId, dpiaId, measureId) => {
    await axios.delete(buildMeasureEndpoint(organizationId, dpiaId, measureId));
  },
  submit: async (organizationId, dpiaId, payload) => {
    const { data } = await axios.post<DpiaDetailResponseDTO>(
      buildSubmitEndpoint(organizationId, dpiaId),
      parseSubmitDpiaForBE(payload.revision),
    );
    return { data: parseDpiaDetailResponseForFE(data) };
  },
  approve: async (organizationId, dpiaId, payload: ApproveDpiaInput) => {
    const { data } = await axios.post<DpiaDetailResponseDTO>(buildApproveEndpoint(organizationId, dpiaId), parseApproveDpiaForBE(payload));
    return { data: parseDpiaDetailResponseForFE(data) };
  },
  requestClarification: async (organizationId, dpiaId, payload: RequestDpiaClarificationInput) => {
    const { data } = await axios.post<DpiaDetailResponseDTO>(
      buildRequestClarificationEndpoint(organizationId, dpiaId),
      parseRequestDpiaClarificationForBE(payload),
    );
    return { data: parseDpiaDetailResponseForFE(data) };
  },
};

export default dpiasApi;
